// Standalone "New Ticket" modal — used for both bug reports AND feature
// requests. Hosted by the SupportPage and by the per-page floating
// SupportTicketButton, so a user can file a ticket from anywhere without
// navigating away from what they were doing.
//
// `source` is a free-form short string ("map", "alerts", "research") that
// the server stamps onto the ticket as a `page:<source>` Zendesk tag —
// useful for support filtering and for understanding which surface a
// problem was reported from.

import React, { useState } from 'react';
import { createSupportTicket, SupportTicketSummary } from '../api/client';

const PALETTE = {
  bgPanel: '#002F5C',
  bgInput: '#004777',
  text:    '#e2e8f0',
  muted:   '#cbd5e1',
  border:  'rgba(255, 255, 255, 0.12)',
  danger:  '#f87171',
};

interface CreateTicketModalProps {
  /** Which page the user opened the modal from. Stored on the ticket as
   *  a `page:<source>` tag so support can filter by origin surface. */
  source?: 'map' | 'alerts' | 'research' | 'support';
  onCancel: () => void;
  onCreated: (ticket: SupportTicketSummary) => void;
}

export const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ source, onCancel, onCreated }) => {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = subject.trim().length > 0 && description.trim().length > 0 && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const { ticket } = await createSupportTicket({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        source,
      });
      onCreated(ticket);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to create ticket');
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000,
    }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div style={{
        width: 'min(560px, 92vw)',
        background: PALETTE.bgPanel,
        border: `1px solid ${PALETTE.border}`,
        borderRadius: 12,
        padding: 24,
        color: PALETTE.text,
        boxShadow: '0 16px 48px rgba(0,0,0,0.45)',
      }}>
        <h2 style={{ margin: 0, marginBottom: 18, fontSize: 18, fontWeight: 600 }}>New Ticket</h2>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Subject</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief summary of the issue or feature request"
            style={inputStyle}
            autoFocus
          />
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the issue (what happened, what you expected, steps to reproduce) or the feature you'd like to see."
            rows={6}
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
          />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Priority</label>
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as typeof priority)}
            style={inputStyle}
          >
            <option value="low" style={{ background: PALETTE.bgInput }}>Low</option>
            <option value="normal" style={{ background: PALETTE.bgInput }}>Normal</option>
            <option value="high" style={{ background: PALETTE.bgInput }}>High</option>
            <option value="urgent" style={{ background: PALETTE.bgInput }}>Urgent</option>
          </select>
        </div>

        {error && (
          <div style={{
            marginBottom: 14, padding: 10,
            background: 'rgba(248,113,113,0.12)',
            border: `1px solid rgba(248,113,113,0.3)`,
            borderRadius: 6,
            color: PALETTE.danger,
            fontSize: 12,
          }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCancel}
            disabled={busy}
            style={{
              background: 'none',
              border: `1px solid ${PALETTE.border}`,
              borderRadius: 6,
              color: PALETTE.muted,
              padding: '8px 16px',
              fontSize: 13,
              cursor: busy ? 'not-allowed' : 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              background: canSubmit ? 'linear-gradient(to right, #006B88, #228B54)' : 'rgba(255,255,255,0.08)',
              border: 'none',
              borderRadius: 6,
              color: 'white',
              padding: '8px 16px',
              fontSize: 13,
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.5,
            }}
          >{busy ? 'Creating…' : 'Create ticket'}</button>
        </div>
      </div>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, color: PALETTE.muted, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
};
const inputStyle: React.CSSProperties = {
  width: '100%',
  background: PALETTE.bgInput,
  border: `1px solid ${PALETTE.border}`,
  borderRadius: 6,
  color: PALETTE.text,
  padding: '9px 12px',
  fontSize: 13,
  outline: 'none',
  boxSizing: 'border-box',
};

export default CreateTicketModal;
